import React, { useState, useEffect } from "react";

import { useHistory } from "react-router-dom";

import { API, graphqlOperation } from "aws-amplify";

import { addMonths, format, startOfMonth, sub } from "date-fns";

import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Typography } from "@material-ui/core";

import { useNotify } from "ra-core";

import { logError } from "../../../utils/appUtils";
import { searchInvoicesForReport } from "../../../graphql/report-queries";
import Title from "../Title";
import { ChargeEventChoices } from "../../resources/accounts/chargeRules/chargeRuleConstants";
import { trimDecimal } from "../../../utils/utils";
import { InvoiceStatus } from "../../../utils/constants";

// We fetch 14 months so we can:
// - Display 13 rolling months (year-over-year window)
// - Use the extra oldest month to compute a 2-month moving average for the first visible month
const TOTAL_MONTHS = 14;
const DISPLAY_MONTHS = 13;
const emptyData = Array.from({ length: TOTAL_MONTHS }, () => ({}));

// const CustomizedLabel = (props) => {
//   const { x, y, fill, value } = props;
//   return (
//     <text
//       x={x}
//       y={y}
//       dy={-4}
//       fontSize="16"
//       fontFamily="sans-serif"
//       fill={fill}
//       textAnchor="middle"
//     >
//       {value}
//     </text>
//   );
// };

export const MonthlyRevenueChart = ({ reloadYardId, ...props }) => {
  const notify = useNotify();
  const history = useHistory();
  const [data, setData] = useState(emptyData);
  const [visibleEvents, setVisibleEvents] = useState([
    ...ChargeEventChoices.map(({ id }) => id),
    "OTHER",
  ]);
  const [isHoverClickable, setIsHoverClickable] = useState(false);
  const [yMax, setYMax] = useState(null);

  useEffect(() => {
    getMonthlyRevenueData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMonthClick = (chartData) => {
    const payload = chartData?.activePayload?.[0]?.payload;
    const monthStart = payload?.monthStart;

    if (payload?.totalAmt > 0) {
      if (monthStart) {
        history.push({
          pathname: "/reportDailyRevenue",
          search: `?monthStart=${encodeURIComponent(monthStart)}`,
        });
      } else {
        history.push("/reportDailyRevenue");
      }
    }
  };

  const getMonthlyRevenueData = async () => {
    // Oldest month is 13 months ago; we fetch 14 months starting from there
    // so we can display the latest 13 months with a 2‑month moving average.
    const startDate = startOfMonth(sub(new Date(), { months: 13 }));
    const tempData = [];

    try {
      const yardId = reloadYardId ? reloadYardId : window.appUser?.reloadYardId;

      const monthPromises = [];

      const fetchMonthTotals = async (monthStartDate, nextMonthDate) => {
        let nextToken;

        // Initialize per-event buckets dynamically from ChargeEventChoices
        const eventTotals = { OTHER: 0 };
        ChargeEventChoices.forEach(({ id }) => {
          eventTotals[id] = 0;
        });

        const baseInput = {
          filter: {
            active: { eq: "true" },
            reloadYardId: { eq: yardId },
            status: { eq: InvoiceStatus.PAID },
            and: [
              { createdAt: { gte: monthStartDate.toISOString() } },
              { createdAt: { lt: nextMonthDate.toISOString() } },
            ],
          },
          sort: { field: "createdAt", direction: "asc" },
          limit: 1000,
        };

        do {
          const input = nextToken ? { ...baseInput, nextToken } : baseInput;

          // eslint-disable-next-line no-await-in-loop
          const result = await API.graphql(
            graphqlOperation(searchInvoicesForReport, input)
          );

          const { items = [], nextToken: newNextToken } =
            result?.data?.searchInvoices || {};

          items.forEach((invoice) => {
            const value = invoice.total || 0;
            const eventKey = invoice.event;

            if (
              eventKey &&
              Object.prototype.hasOwnProperty.call(eventTotals, eventKey)
            ) {
              eventTotals[eventKey] += value;
            } else {
              eventTotals.OTHER += value;
            }
          });

          nextToken = newNextToken;
        } while (nextToken);

        return eventTotals;
      };

      for (let i = 0; i < TOTAL_MONTHS; i++) {
        const monthStartDate = addMonths(startDate, i);
        const nextMonthDate = addMonths(monthStartDate, 1);
        const monthName = format(monthStartDate, "MMM");
        const options = { month: "2-digit", year: "numeric" };
        const barDate = monthStartDate.toLocaleDateString("en-US", options);

        tempData[i] = {
          name: monthName,
          date: barDate,
          monthStart: monthStartDate.toISOString(),
          amt: 0,
        };

        monthPromises[i] = fetchMonthTotals(monthStartDate, nextMonthDate);
      }

      const monthResults = await Promise.all(monthPromises);

      monthResults.forEach((eventTotals, idx) => {
        const monthTotal = Object.values(eventTotals).reduce(
          (sum, v) => sum + (v || 0),
          0
        );

        tempData[idx] = {
          ...tempData[idx],
          ...eventTotals,
          totalAmt: monthTotal,
        };
      });

      // Compute 2-month moving average of total revenue
      tempData.forEach((month, idx) => {
        if (idx === 0) {
          tempData[idx].movingAvg = null;
        } else {
          const prevTotal = tempData[idx - 1].totalAmt || 0;
          const currTotal = tempData[idx].totalAmt || 0;
          const avg = trimDecimal((prevTotal + currTotal) / 2, 2);
          tempData[idx].movingAvg = avg;
        }
      });

      // Determine which events actually have data across all months
      const allEventIds = ChargeEventChoices.map(({ id }) => id);
      const nextVisibleEvents = [];

      allEventIds.forEach((id) => {
        const hasValue = tempData.some((month) => (month[id] || 0) !== 0);
        if (hasValue) {
          nextVisibleEvents.push(id);
        }
      });

      const hasOther = tempData.some((month) => (month.OTHER || 0) !== 0);
      if (hasOther) {
        nextVisibleEvents.push("OTHER");
      }

      // Compute Y-axis max based on visible window (bars + moving average)
      const displayData = tempData.slice(TOTAL_MONTHS - DISPLAY_MONTHS);
      let computedMax = 0;
      displayData.forEach((month) => {
        const seriesValues = [
          ...ChargeEventChoices.map(({ id }) => month[id] || 0),
          month.OTHER || 0,
          month.totalAmt || 0,
          month.movingAvg || 0,
        ];
        const monthMax = Math.max(...seriesValues);
        if (monthMax > computedMax) {
          computedMax = monthMax;
        }
      });

      setData(tempData);
      setVisibleEvents(nextVisibleEvents);
      setYMax(computedMax || 0);
    } catch (err) {
      logError("MonthlyRevenue:getMonthlyRevenueData", err, notify);
    }
  };

  return (
    <>
      <Title>Monthly Revenue</Title>
      <ResponsiveContainer>
        <ComposedChart
          // Display only the most recent 13 months; the oldest
          // month is kept only to support the 2‑month moving average.
          data={data.slice(TOTAL_MONTHS - DISPLAY_MONTHS)}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          barCategoryGap="20%"
          onClick={handleMonthClick}
          onMouseMove={(state) => {
            const totalAmt =
              state?.activePayload?.[0]?.payload?.totalAmt != null
                ? state.activePayload[0].payload.totalAmt
                : 0;
            setIsHoverClickable(totalAmt > 0);
          }}
          onMouseLeave={() => setIsHoverClickable(false)}
          style={{ cursor: isHoverClickable ? "pointer" : "default" }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" tickSize dy="25" />
          <YAxis domain={[0, yMax ? yMax * 1.1 : "auto"]} />
          <Legend />
          <Tooltip
            content={({ payload }) => {
              if (payload && payload.length) {
                const { date, totalAmt = 0, OTHER = 0 } = payload[0].payload;
                return (
                  <div
                    style={{
                      color: "black",
                      background: "white",
                      padding: "8px",
                      border: "1px solid black",
                    }}
                  >
                    <Typography>{`Month: ${date}`}</Typography>
                    {ChargeEventChoices.filter(({ id }) =>
                      visibleEvents.includes(id)
                    ).map(({ id, name }) => (
                      <Typography key={id}>{`${name}: ${
                        payload[0].payload[id] || 0
                      }`}</Typography>
                    ))}
                    {visibleEvents.includes("OTHER") && OTHER ? (
                      <Typography>{`Other: ${OTHER}`}</Typography>
                    ) : null}
                    <Typography>{`Total: ${totalAmt}`}</Typography>
                    <Typography>{`Moving Avg: ${
                      payload[0].payload.movingAvg != null
                        ? payload[0].payload.movingAvg
                        : "N/A"
                    }`}</Typography>
                  </div>
                );
              }
              return null;
            }}
          />
          {ChargeEventChoices.filter(({ id }) =>
            visibleEvents.includes(id)
          ).map(({ id, name }, index) => {
            const palette = [
              "#4caf50",
              "#2196f3",
              "#ff9800",
              "#9e9e9e",
              "#ab47bc",
            ];
            const fill = palette[index % palette.length];
            return (
              <Bar
                key={id}
                dataKey={id}
                name={name}
                fill={fill}
                cursor="pointer"
                // label={<CustomizedLabel />}
              />
            );
          })}
          {visibleEvents.includes("OTHER") && (
            <Bar
              key="OTHER"
              dataKey="OTHER"
              name="Other"
              fill="#bdbdbd"
              // label={<CustomizedLabel />}
            />
          )}
          <Line
            type="monotone"
            dataKey="movingAvg"
            name="2-Month Moving Avg"
            stroke="#f44336"
            strokeWidth={3}
            dot={false}
            connectNulls={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </>
  );
};
